import { useState, type MouseEvent } from 'react';
import { useSession } from '../lib/session';
import { toggleWatch } from '../lib/queries';
import './WatchButton.css';

interface WatchButtonProps {
  postId: string;
  initialWatching: boolean;
  /** Pass watch_count from the auction state. */
  initialCount: number;
  /** compact = icon + count only; full = labeled. Defaults to 'compact'. */
  variant?: 'compact' | 'full';
  className?: string;
  /** When false (e.g. an ended lot), you can switch watching OFF but not back
   *  ON — the button only renders while currently watching, then disappears. */
  canWatch?: boolean;
}

/**
 * Heart-toggle button that flips watch state on a post.
 *
 * Optimistic: local watching + count update first, then call the toggleWatch
 * query and roll back on error. Disabled (still rendered) when there is no
 * signed-in profile so the button can sit on a Sales Room card without
 * crashing for not-yet-loaded sessions.
 *
 * Visuals match the polls/like pulse — ♡ at rest, ♥ in accent when on.
 */
export function WatchButton({
  postId,
  initialWatching,
  initialCount,
  variant = 'compact',
  className,
  canWatch = true,
}: WatchButtonProps) {
  const { profile } = useSession();
  const [watching, setWatching] = useState(initialWatching);
  const [count, setCount] = useState(initialCount);
  const [busy, setBusy] = useState(false);

  const disabled = !profile || busy;

  async function onClick(e: MouseEvent<HTMLButtonElement>) {
    // The button often sits inside a <Link to=...> wrapping the whole card.
    // Stop the click bubbling so the navigation doesn't fire when the heart
    // is tapped.
    e.preventDefault();
    e.stopPropagation();
    if (!profile || busy) return;

    const prevWatching = watching;
    const prevCount = count;
    // Optimistic flip
    setWatching(!prevWatching);
    setCount(prevCount + (prevWatching ? -1 : 1));
    setBusy(true);
    try {
      await toggleWatch(postId, profile.id, prevWatching);
    } catch (err) {
      console.warn('[WatchButton] toggle failed', err);
      // Roll back
      setWatching(prevWatching);
      setCount(prevCount);
    } finally {
      setBusy(false);
    }
  }

  // Ended lot: once you've stopped watching there's no watching it again, so
  // the control simply isn't there for non-watchers.
  if (!canWatch && !watching) return null;

  const label = watching ? 'Stop watching' : 'Watch this lot';

  return (
    <button
      type="button"
      className={`watch-btn watch-btn--${variant} ${watching ? 'watch-btn--on' : ''} ${className ?? ''}`}
      onClick={onClick}
      disabled={disabled}
      aria-pressed={watching}
      aria-label={label}
      title={label}
    >
      <span className="watch-btn__heart" aria-hidden="true"><EyeIcon on={watching} /></span>
      {variant === 'full' ? (
        <span className="watch-btn__label">
          {watching ? 'Watching' : 'Watch'}
          <span aria-hidden="true"> &middot; </span>
          <span className="watch-btn__count">{count}</span>
        </span>
      ) : (
        <span className="watch-btn__count">{count}</span>
      )}
    </button>
  );
}

/** Watch glyph — an eye. Pupil fills when actively watching.
 *  Also reused by the Saleroom header's Watchlist pill. */
export function EyeIcon({ on = false }: { on?: boolean }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <circle
        cx="12"
        cy="12"
        r="3"
        fill={on ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="1.8"
      />
    </svg>
  );
}
